import { useState, useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import apiService from '@/services/api';

type RankType = 'CHALLENGER' | 'MASTER';

interface MissionRankBadgeProps {
  userId: number;
  showLabel?: boolean;
  size?: 'xs' | 'sm' | 'md';
  /** 다크 테마 (관리자 페이지 기본), false면 라이트 테마 */
  dark?: boolean;
  /** 초기 등급값. undefined가 아니면 이 값을 그대로 쓰고 네트워크 조회를 생략한다. */
  initialRank?: RankType | null;
}

// 작은 SVG 아이콘 — useId로 고유 그라데이션 ID 생성 (DOM 충돌 방지)
const RankIcon = ({ type, size }: { type: RankType; size: number }) => {
  const uid = useId().replace(/:/g, '');
  if (type === 'CHALLENGER') {
    const gid = `arbCh-${uid}`;
    return (
      <svg viewBox="0 0 64 64" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#93c5fd" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
        </defs>
        <path
          d="M32 6 L52 14 L51 33 C49 45 32 57 32 57 C32 57 15 45 13 33 L12 14 Z"
          fill={`url(#${gid})`}
          stroke="#ffffff"
          strokeWidth="2"
          strokeLinejoin="round"
        />
        <path d="M20 22 L42 42 M44 22 L22 42" stroke="#ffffff" strokeWidth="2.8" strokeLinecap="round" />
        <path
          d="M32 25 L33.5 29 L37.5 29.4 L34.5 32 L35.4 36 L32 33.9 L28.6 36 L29.5 32 L26.5 29.4 L30.5 29 Z"
          fill="#fde047"
          stroke="#fbbf24"
          strokeWidth="0.6"
        />
      </svg>
    );
  }
  const gid = `arbMs-${uid}`;
  return (
    <svg viewBox="0 0 64 64" width={size} height={size} fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fef08a" />
          <stop offset="50%" stopColor="#fbbf24" />
          <stop offset="100%" stopColor="#d97706" />
        </linearGradient>
      </defs>
      <path
        d="M10 24 L18 38 L24 18 L32 34 L40 18 L46 38 L54 24 L52 46 L12 46 Z"
        fill={`url(#${gid})`}
        stroke="#ffffff"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <rect x="11" y="44" width="42" height="6" rx="1.5" fill={`url(#${gid})`} stroke="#ffffff" strokeWidth="2" />
      <circle cx="32" cy="30" r="3.5" fill="#dc2626" stroke="#ffffff" strokeWidth="1.2" />
      <circle cx="18" cy="36" r="1.8" fill="#22d3ee" stroke="#ffffff" strokeWidth="0.6" />
      <circle cx="46" cy="36" r="1.8" fill="#22d3ee" stroke="#ffffff" strokeWidth="0.6" />
    </svg>
  );
};

// 메모리 캐시 (한 번 조회한 등급은 재사용 — 캐시에 있으면 네트워크 조회 생략)
const _rankCache = new Map<number, RankType>();
// 같은 userId에 대한 동시 요청 중복 제거 — 여러 뱃지가 한 번에 마운트돼도 요청 1건 공유
const _inflight = new Map<number, Promise<RankType>>();

/**
 * 관리자 페이지용 미션 등급 뱃지
 * - 리스트에서는 showLabel=false (아이콘만)
 * - 상세에서는 showLabel=true (아이콘 + 텍스트)
 * - dark=true (기본) → 다크 테마 스타일
 */
export default function MissionRankBadge({ userId, showLabel = false, size = 'sm', dark = true, initialRank }: MissionRankBadgeProps) {
  const { i18n } = useTranslation();
  const [rank, setRank] = useState<RankType | null>(
    initialRank !== undefined ? initialRank : (_rankCache.get(userId) ?? null)
  );

  useEffect(() => {
    if (!userId) return;

    // initialRank가 주어지면 그대로 사용하고 네트워크 조회 생략
    if (initialRank !== undefined) {
      if (initialRank) _rankCache.set(userId, initialRank);
      setRank(initialRank);
      return;
    }

    // 이미 캐시된 값이 있으면 그대로 쓰고 네트워크 조회 생략
    const cached = _rankCache.get(userId);
    if (cached) {
      setRank(cached);
      return;
    }

    // 캐시에 없을 때만 fetch. 같은 userId 동시 요청은 하나의 Promise를 공유(중복 제거)
    let cancelled = false;
    let promise = _inflight.get(userId);
    if (!promise) {
      promise = (async () => {
        try {
          const resp: any = await apiService.get(`/api/v1/admin/users/${userId}/mission-rank`);
          // apiService.get은 이미 response.data를 풀어주지만 양쪽 형태 대응
          const rankValue = resp?.rank ?? resp?.data?.rank;
          const r: RankType = rankValue === 'MASTER' ? 'MASTER' : 'CHALLENGER';
          _rankCache.set(userId, r);
          return r;
        } finally {
          _inflight.delete(userId);
        }
      })();
      _inflight.set(userId, promise);
    }

    promise
      .then((r) => {
        if (!cancelled) setRank(r);
      })
      .catch(() => {
        if (!cancelled && !_rankCache.has(userId)) {
          _rankCache.set(userId, 'CHALLENGER');
          setRank('CHALLENGER');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userId, initialRank]);

  if (!rank) return null;

  const isMaster = rank === 'MASTER';
  const iconSize = size === 'xs' ? 14 : size === 'sm' ? 18 : 22;

  // 한국어면 한글, 그 외(en/zh/ja)는 영문
  const isKorean = (i18n.language || 'ko').startsWith('ko');
  const label = isMaster
    ? (isKorean ? '마스터' : 'MASTER')
    : (isKorean ? '챌린저' : 'CHALLENGER');

  if (showLabel) {
    // 다크 테마 우선
    const darkStyle = isMaster
      ? 'bg-amber-500/15 text-amber-300 border-amber-400/40'
      : 'bg-indigo-500/15 text-indigo-300 border-indigo-400/40';
    const lightStyle = isMaster
      ? 'bg-white text-amber-700 border-amber-300 shadow-sm'
      : 'bg-white text-indigo-700 border-indigo-300 shadow-sm';

    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] sm:text-[11px] font-extrabold tracking-wide whitespace-nowrap border ${
          dark ? darkStyle : lightStyle
        }`}
      >
        <RankIcon type={rank} size={iconSize} />
        <span>{label}</span>
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center align-middle"
      title={label}
    >
      <RankIcon type={rank} size={iconSize} />
    </span>
  );
}
